/**
 * Admin Secrets Export — one-time self-serve credentials vault download.
 *
 * Doctrine (20-Jun-2026 · TDC pattern · Dipali):
 *   1) CEO arrives on /admin/secrets/export
 *   2) Re-enters their password (proves identity even on a valid session)
 *   3) Backend mints a one-time token (5-min expiry, 1 use max)
 *   4) Click "Download" → file streams · token burned in same request
 *
 * Two-screen flow keeps it deliberately friction-y (this is the most
 * sensitive endpoint in the system).
 */
import React, { useState } from "react";
import { Shield, Lock, Download, CheckCircle2, AlertTriangle, Loader2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => {
  const tok = localStorage.getItem("access_token") || localStorage.getItem("token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

export default function AdminSecretsExport() {
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [token, setToken] = useState(null);
  const [expiresInSec, setExpiresInSec] = useState(0);
  const [error, setError] = useState(null);
  const [downloaded, setDownloaded] = useState(false);

  // Countdown timer once we have a token
  React.useEffect(() => {
    if (!token || downloaded) return;
    const t = setInterval(() => {
      setExpiresInSec((s) => {
        if (s <= 1) {
          clearInterval(t);
          setToken(null);
          setError("Token expired — please re-verify your password to generate a new one.");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [token, downloaded]);

  const requestToken = async (e) => {
    e?.preventDefault();
    if (verifying || !password) return;
    setError(null);
    setVerifying(true);
    try {
      const res = await fetch(`${API}/admin/secrets/request`, {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setToken(data.token);
        setExpiresInSec(data.expires_in_seconds || 300);
        setPassword("");
      } else {
        setError(data.detail || "Password verification failed. Try again.");
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setVerifying(false);
    }
  };

  const downloadNow = () => {
    if (!token) return;
    const url = `${API}/admin/secrets/download/${token}`;
    // Build a hidden anchor + click so we can include auth header via fetch+blob
    fetch(url, { headers: headers(), credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "ScaleBoard_Credentials_Vault.docx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        setDownloaded(true);
        setToken(null);
      })
      .catch((err) => setError(`Download failed: ${err.message}`));
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAF8F2" }} data-testid="admin-secrets-export-page">
      <div className="max-w-xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Shield size={26} style={{ color: "#7A2B2B" }} />
          <h1 className="text-2xl font-semibold" style={{ color: "#15110D" }}>Credentials Vault · Export</h1>
        </div>
        <p className="text-sm text-gray-600 mb-8">
          Generates a one-time download link for the ScaleBoard secrets document.
          Link expires in 5 minutes and works only once.
        </p>

        {/* Always-visible security strip */}
        <div
          className="rounded-lg px-4 py-3 mb-8 text-xs flex gap-2 items-start"
          style={{ background: "#FDECEC", color: "#7A2B2B", borderLeft: "3px solid #DC2626" }}
        >
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <strong>This document contains every API key, login, and integration secret.</strong>
            <br />
            Save it directly into 1Password (or equivalent) and delete the file from your Downloads folder
            right after.  Never email it, never save on shared drives.
          </div>
        </div>

        {downloaded ? (
          /* ── Success state ─────────────────────────────────────── */
          <div className="rounded-2xl border border-[#C8E5D2] bg-white p-8 text-center" data-testid="download-success">
            <CheckCircle2 size={48} style={{ color: "#16A34A" }} className="mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-2" style={{ color: "#15110D" }}>Downloaded</h2>
            <p className="text-sm text-gray-600 mb-6">
              Move it into your encrypted vault now, then delete the file from your Downloads folder.
            </p>
            <button
              onClick={() => { setDownloaded(false); setError(null); }}
              className="text-xs text-gray-500 hover:text-[#15110D] underline"
              data-testid="reset-form"
            >
              Generate another link
            </button>
          </div>
        ) : !token ? (
          /* ── Re-auth screen ────────────────────────────────────── */
          <form onSubmit={requestToken} className="rounded-2xl border border-[#E8DFD0] bg-white p-8" data-testid="reauth-form">
            <label className="block text-sm font-medium mb-2" style={{ color: "#15110D" }}>
              <Lock size={14} className="inline mr-1" />
              Re-enter your password
            </label>
            <p className="text-xs text-gray-500 mb-4">
              We require fresh password verification even though you&apos;re logged in.  A hijacked session
              alone is not enough to download this document.
            </p>
            <div className="relative mb-5">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
                data-testid="reauth-password-input"
                className="w-full px-4 py-3 pr-10 rounded-lg border border-gray-300 focus:border-[#15110D] focus:outline-none text-sm"
                placeholder="Your ScaleBoard password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                data-testid="reauth-toggle-visibility"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {error && (
              <div
                className="px-4 py-3 rounded-lg text-xs mb-4"
                data-testid="reauth-error"
                style={{ background: "#FDECEC", color: "#7A2B2B", borderLeft: "3px solid #DC2626" }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!password || verifying}
              data-testid="reauth-submit"
              className="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "#15110D" }}
            >
              {verifying ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Verifying…</span>
              ) : (
                "Verify & Generate One-Time Link"
              )}
            </button>
          </form>
        ) : (
          /* ── Token-ready / download screen ──────────────────────── */
          <div className="rounded-2xl border border-[#E8DFD0] bg-white p-8" data-testid="download-screen">
            <div className="text-center mb-6">
              <CheckCircle2 size={32} style={{ color: "#16A34A" }} className="mx-auto mb-2" />
              <div className="text-sm font-semibold" style={{ color: "#15110D" }}>
                Identity verified
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Link valid for{" "}
                <span className="font-mono" data-testid="countdown" style={{ color: expiresInSec < 30 ? "#7A2B2B" : "#15110D" }}>
                  {Math.floor(expiresInSec / 60)}:{String(expiresInSec % 60).padStart(2, "0")}
                </span>
                {" · "}1 download only
              </div>
            </div>

            <button
              onClick={downloadNow}
              data-testid="download-now-btn"
              className="w-full px-6 py-4 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all hover:scale-[1.02] shadow-md"
              style={{ background: "#16A34A" }}
            >
              <Download size={16} /> Download Credentials Vault (.docx)
            </button>

            <div className="text-xs text-gray-400 mt-4 text-center">
              After download, the link is automatically burned and cannot be re-used.
              <br />
              No copy is saved on disk or Drive — the file streams straight to your browser.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
